/**
 * Atelier Royal: global.js (ES module, every page; import map name "@theme/global").
 *
 * Registers <ar-drawer> and <ar-dialog> (native <dialog> + focus trap, Esc, focus return,
 * inert background via showModal); wires the cart count, the cart-drawer
 * bridge, toasts, screen-reader announcements, analytics tracking, localization forms,
 * responsive <details> and rotating text. Exports helpers for section modules.
 * Contract: docs/theme/CONVENTIONS.md
 */

/* ---------- Theme data (printed by layout/theme.liquid) ---------- */
function readThemeData() {
  const el = document.getElementById('ARThemeData');
  if (!el) return {};
  try {
    const data = JSON.parse(el.textContent || '{}');
    /* `t` HTML-escapes translations (l'atelier → l&#39;atelier); strings reach the DOM as text, so decode them once. */
    const map = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
    for (const [k, v] of Object.entries(data.strings || {})) {
      if (typeof v === 'string') data.strings[k] = v.replace(/&(?:amp|lt|gt|quot|#39);/g, (m) => map[m]);
    }
    return data;
  } catch {
    return {};
  }
}

export const theme = readThemeData();
export const routes = theme.routes || {};
export const strings = theme.strings || {};

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Focus helpers ---------- */
const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'summary',
  '[tabindex]:not([tabindex="-1"])', '[contenteditable="true"]',
].join(',');

export function getFocusable(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => !el.closest('[inert]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'
  );
}

/** Keeps Tab / Shift+Tab inside `container`. Call from a keydown listener. */
export function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const items = getFocusable(container);
  if (!items.length) {
    event.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !container.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !container.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}

/* ---------- Analytics (spec §57; consent handled by Shopify Customer Privacy) ---------- */
/**
 * Publishes `atelier:<name>` to Shopify's analytics bus, where the merchant custom pixel
 * (shopify-config/pixels/atelier-custom-pixel.js) forwards it to Meta and GA4 with consent.
 * Never pass images, URLs or personal data. The `ar:track` DOM event mirrors every call as a
 * debugging hook only (nothing in the theme listens to it).
 */
export function track(name, data = {}) {
  try {
    window.Shopify?.analytics?.publish?.(`atelier:${name}`, data);
  } catch {
    /* analytics must never break the page */
  }
  document.dispatchEvent(new CustomEvent('ar:track', { detail: { name, data } }));
}

/**
 * Analytics fields of a variant-map entry (snippets/variant-map-json): Shopify ids as strings, the
 * price in currency units and the ISO currency, so a pixel can send ViewContent / view_item with
 * catalog ids and a value. Returns {} without a variant.
 */
export function trackVariant(v) {
  if (!v?.id) return {};
  const cents = Number(v.price);
  return {
    variant_id: String(v.id),
    product_id: v.product_id ? String(v.product_id) : null,
    price: Number.isFinite(cents) ? cents / 100 : null,
    currency: theme.currency || null,
  };
}

/* ---------- Screen-reader announcements & toasts ---------- */
/** Announces an already-translated message through the persistent live regions. */
export function announce(message, politeness = 'polite') {
  const region = document.querySelector(`[data-ar-announcer="${politeness === 'assertive' ? 'assertive' : 'polite'}"]`);
  if (!region || !message) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

/**
 * Shows a toast. `message` must already be translated (from Liquid / theme strings).
 * The renderer (toast.js) is loaded on first use (import on interaction).
 * @param {string} message
 * @param {{tone?: 'info'|'success'|'error', timeout?: number}} [options]
 */
export function toast(message, options = {}) {
  if (!message) return;
  import('@theme/toast').then(({ showToast }) => showToast(message, options)).catch(() => {});
  announce(message, options.tone === 'error' ? 'assertive' : 'polite');
}

document.addEventListener('ar:toast', (event) => {
  const { message, tone, timeout } = event.detail || {};
  toast(message, { tone, timeout });
});

/* ---------- Modals: <ar-drawer> and <ar-dialog> ---------- */
const openModals = new Set();

class ARModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector(':scope > dialog');
    if (!this.dialog || this.dataset.arReady) return;
    this.dataset.arReady = 'true';

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    // Chromium dispatches `close` on the next animation frame; hide() cleans up synchronously
    // and this listener only covers other ways of closing (e.g. <form method="dialog">).
    this.dialog.addEventListener('close', () => {
      if (!this.dialog.open) this.#onClosed(); // ignore a stale event after a quick re-open
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.hide(); // backdrop click
    });
    this.dialog.addEventListener('keydown', (event) => trapFocus(this.dialog, event));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ar-close]')) {
        event.preventDefault();
        this.hide();
      }
    });
  }

  disconnectedCallback() {
    if (openModals.has(this)) {
      openModals.delete(this);
      if (!openModals.size) document.documentElement.removeAttribute('data-ar-modal');
    }
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  /** Opens the modal. `trigger` receives focus back on close. */
  show(trigger = document.activeElement) {
    if (!this.dialog || this.isOpen) return;
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.dialog.showModal(); // top layer: everything else becomes inert
    openModals.add(this);
    document.documentElement.setAttribute('data-ar-modal', this.id || 'modal');
    this.#setExpanded(true);
    const initial = this.dialog.querySelector('[data-ar-initial-focus]');
    if (initial) initial.focus();
    this.dispatchEvent(new CustomEvent('ar:modal:open', { bubbles: true, detail: { id: this.id, modal: this } }));
  }

  hide() {
    if (!this.isOpen) return;
    this.dialog.close();
    this.#onClosed();
  }

  toggle(trigger) {
    if (this.isOpen) this.hide();
    else this.show(trigger);
  }

  #onClosed() {
    if (!openModals.has(this)) return; // already cleaned up
    openModals.delete(this);
    if (!openModals.size) document.documentElement.removeAttribute('data-ar-modal');
    this.#setExpanded(false);
    const trigger = this.trigger;
    this.trigger = null;
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('ar:modal:close', { bubbles: true, detail: { id: this.id, modal: this } }));
  }

  #setExpanded(expanded) {
    if (!this.id) return;
    document
      .querySelectorAll(`[data-ar-open="${CSS.escape(this.id)}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
  }
}

if (!customElements.get('ar-drawer')) customElements.define('ar-drawer', class ARDrawer extends ARModal {});
if (!customElements.get('ar-dialog')) customElements.define('ar-dialog', class ARDialog extends ARModal {});

/* Openers: <button data-ar-open="MenuDrawer" aria-controls="MenuDrawer" aria-expanded="false"> */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-ar-open]');
  if (!opener) return;
  const modal = document.getElementById(opener.dataset.arOpen);
  if (typeof modal?.toggle !== 'function') return;
  event.preventDefault();
  modal.toggle(opener);
});

/* ---------- Cart: count, open bridge, helpers ---------- */
export async function getCart() {
  try {
    const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

export function updateCartCount(count) {
  const n = Number(count) || 0;
  document.querySelectorAll('[data-ar-cart-count]').forEach((el) => {
    el.textContent = String(n);
    el.hidden = n === 0;
  });
  const rules = new Intl.PluralRules(document.documentElement.lang || 'en');
  document.querySelectorAll('[data-ar-cart-label]').forEach((el) => {
    const template = rules.select(n) === 'one' ? el.dataset.labelOne : el.dataset.labelOther;
    if (template) el.textContent = template.replace('[count]', String(n));
  });
}

/**
 * Opens the cart. Dispatches a cancelable `ar:cart:open`; if nothing handles it
 * (cart type "page", or no drawer on this template) the browser goes to /cart.
 */
export function openCart(trigger) {
  const handled = !document.dispatchEvent(
    new CustomEvent('ar:cart:open', { cancelable: true, detail: { trigger } })
  );
  if (!handled && routes.cart_url) window.location.assign(routes.cart_url);
}

/* Default handler: open <ar-drawer data-ar-cart-drawer>. A custom drawer may handle
   `ar:cart:open` itself (and call preventDefault), this handler then does nothing. */
document.addEventListener('ar:cart:open', (event) => {
  if (event.defaultPrevented) return;
  const drawer = document.querySelector('ar-drawer[data-ar-cart-drawer]');
  if (!drawer || typeof drawer.show !== 'function') return;
  event.preventDefault();
  drawer.show(event.detail?.trigger);
});

/* Header cart links: <a href="{{ routes.cart_url }}" data-ar-cart-trigger> (works without JS) */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-ar-cart-trigger]');
  if (!link || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const handled = !document.dispatchEvent(
    new CustomEvent('ar:cart:open', { cancelable: true, detail: { trigger: link } })
  );
  if (handled) event.preventDefault();
});

document.addEventListener('ar:cart:updated', async (event) => {
  const detail = event.detail || {};
  let count = detail.cart?.item_count ?? detail.item_count;
  if (typeof count !== 'number') count = (await getCart())?.item_count ?? 0;
  updateCartCount(count);
});

/* Back/forward cache: refresh the count when the page is restored */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  getCart().then((cart) => {
    if (cart) updateCartCount(cart.item_count);
  });
});

/* ---------- Details helpers ---------- */
/* details[data-ar-open-above="750"]: open at and above that width (footer columns). */
function syncResponsiveDetails() {
  document.querySelectorAll('details[data-ar-open-above]').forEach((details) => {
    const query = window.matchMedia(`(min-width: ${parseInt(details.dataset.arOpenAbove, 10) || 750}px)`);
    const summary = details.querySelector(':scope > summary');
    const apply = () => {
      details.open = query.matches;
      /* Forced open, the summary is a plain column heading: keep it out of the tab order. */
      if (summary) summary.tabIndex = query.matches ? -1 : 0;
    };
    apply();
    if (!details.dataset.arBound) {
      details.dataset.arBound = 'true';
      query.addEventListener('change', apply);
    }
  });
}
syncResponsiveDetails();

/* details[data-ar-dropdown]: one open at a time; closes on outside click and Esc. */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-ar-dropdown]') || !details.open) return;
    document.querySelectorAll('details[data-ar-dropdown][open]').forEach((other) => {
      if (other !== details && !other.contains(details)) other.open = false;
    });
  },
  true
);
document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-ar-dropdown][open]').forEach((details) => {
    if (!details.contains(event.target)) details.open = false;
  });
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const details = event.target.closest?.('details[data-ar-dropdown][open]');
  if (!details) return;
  details.open = false;
  details.querySelector('summary')?.focus();
});

/* ---------- Localization forms submit on change ----------
   A language picked in any language form (header, menu drawer, holding page buttons) is remembered
   as ar:lang, so the layout's French redirect never overrides it (theme/README.md "Language"). */
const rememberLanguage = (code) => {
  if (!code) return;
  try {
    localStorage.setItem('ar:lang', code);
  } catch {
    /* storage blocked: the choice still applies to this page */
  }
};
/* Leaving French drops the French-link marker (lang=fr, utm_content=fr) from the page Shopify returns to, so an
   English URL copied afterwards does not open in French for someone else (theme/README.md "Language"). */
const dropFrenchMarker = (form, code) => {
  const input = form?.querySelector('input[name="return_to"]');
  if (!code || code === 'fr' || !input?.value) return;
  try {
    const url = new URL(input.value, location.origin);
    const marked = ['lang', 'utm_content'].filter((name) => /^fr([-_][a-z]{2})?$/i.test(url.searchParams.get(name) || ''));
    if (!marked.length) return;
    marked.forEach((name) => url.searchParams.delete(name));
    input.value = url.pathname + url.search + url.hash;
  } catch {
    /* leave return_to as Shopify printed it */
  }
};
document.addEventListener('change', (event) => {
  const form = event.target.closest?.('form[data-ar-localization]');
  if (!form || !event.target.matches('select')) return;
  if (event.target.name === 'language_code') {
    rememberLanguage(event.target.value);
    dropFrenchMarker(form, event.target.value);
  }
  form.submit();
});
document.addEventListener(
  'submit',
  (event) => {
    const button = event.submitter;
    if (button?.name === 'language_code' && event.target.querySelector?.('input[name="form_type"][value="localization"]')) {
      rememberLanguage(button.value);
      dropFrenchMarker(event.target, button.value);
    }
  },
  true
);

/* ---------- Language folder ----------
   On a French page every storefront link and form must stay under /fr. The layout fixes what it prints
   (snippets/localize-links, needed since Shopify's links lost /fr on 2026-09-28); links and forms added
   later (cart drawer after a cart update, search results) are fixed here when clicked or submitted.
   theme.localeRoot is '' in English. */
const localeRoot = theme.localeRoot || '';
const STOREFRONT_PATH = /^\/(?:$|pages\/|products\/|collections(?:\/|$)|blogs\/|policies\/|search(?:\/|$)|cart(?:\/|$)|account(?:\/|$)|contact(?:\/|$))/;
const outsideFolder = (url) =>
  url.origin === window.location.origin &&
  url.pathname !== localeRoot &&
  !url.pathname.startsWith(`${localeRoot}/`) &&
  STOREFRONT_PATH.test(url.pathname);
const intoFolder = (url) => {
  url.pathname = localeRoot + (url.pathname === '/' ? '' : url.pathname);
  return url;
};
if (localeRoot) {
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link) return;
      const url = new URL(link.href);
      if (outsideFolder(url)) link.href = intoFolder(url).href;
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const url = new URL(form.getAttribute('action') || window.location.href, window.location.href);
      if (outsideFolder(url)) form.setAttribute('action', intoFolder(url).href);
    },
    true
  );
}

/* ---------- Rotating text: [data-ar-rotate="3200"] cycles .is-active on its children ---------- */
function startRotators(root = document) {
  if (prefersReducedMotion()) return;
  root.querySelectorAll('[data-ar-rotate]').forEach((el) => {
    if (el.dataset.arRotating) return;
    const items = [...el.children];
    if (items.length < 2) return;
    el.dataset.arRotating = 'true';
    let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    window.setInterval(() => {
      if (document.hidden || !el.isConnected) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, parseInt(el.dataset.arRotate, 10) || 3200);
  });
}
startRotators();
export { startRotators };

/* ---------- Portrait protection (deterrent only, spec §68) ----------
   Images inside [data-protect] (creator preview, version picker, compare dialog, cart summary, cart
   line thumbnails): no context menu (right click, Android long-press) and no drag. The iOS long-press
   callout is switched off in CSS (-webkit-touch-callout: none). */
const isProtected = (event) => event.target instanceof Element && event.target.closest('[data-protect]');
document.addEventListener('contextmenu', (event) => {
  if (isProtected(event)) event.preventDefault();
});
document.addEventListener('dragstart', (event) => {
  if (isProtected(event)) event.preventDefault();
});

/* ---------- Click tracking: data-ar-track="click_create_portrait" ---------- */
document.addEventListener('click', (event) => {
  const el = event.target.closest('[data-ar-track]');
  if (el) track(el.dataset.arTrack, { location: el.dataset.arTrackLocation || document.body.dataset.template || '' });
});

/* ---------- Theme editor ---------- */
document.addEventListener('shopify:section:load', (event) => {
  syncResponsiveDetails();
  startRotators(event.target);
});
document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelector('ar-drawer[data-ar-editor-open], ar-dialog[data-ar-editor-open]')?.show();
});
document.addEventListener('shopify:section:deselect', (event) => {
  event.target.querySelector('ar-drawer[data-ar-editor-open], ar-dialog[data-ar-editor-open]')?.hide();
});

/* Non-module access (inline scripts, apps): window.AR */
window.AR = Object.freeze({
  theme, routes, strings, track, toast, announce, openCart, getCart, updateCartCount, trapFocus, getFocusable,
});
